import { DateTime } from 'luxon';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import * as events from '../../lib/analytics/events';
import { cartActions as storeCartActions } from '../../reducers/storeCart';
import { validateEmail } from '../../lib/util';
import { IGiftCardProduct } from '../../types';
import { FormErrors, IGiftCardState } from './GiftCardContext';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../contexts/LocaleContext';

interface ICheckoutHookResponse {
  addToBag: (product: IGiftCardProduct, state: IGiftCardState) => void;
  addedToBag: boolean;
}

export default function useCheckout(setErrors: (errors: FormErrors) => void): ICheckoutHookResponse {
  const [addedToBag, setAddedToBag] = useState(false);
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const { currency } = useLocale();

  const addToBag = (product: IGiftCardProduct, state: IGiftCardState) => {
    const errors: FormErrors = {};
    const { delivery, deliverAtIsSet } = state;

    if (!delivery.firstName) {
      errors.gifteeName = t('please_enter_a_name_for_the_giftee');
    }
    if (!validateEmail(delivery.emailAddress)) {
      errors.emailAddress = t('please_enter_a_valid_email_address');
    }
    if (!delivery.gifterName) {
      errors.gifterName = t('please_enter_your_name');
    }
    const genericDeliverAtError = t('please_enter_a_date_in_the_future_or_select_now');
    if (!deliverAtIsSet) {
      errors.deliverAt = genericDeliverAtError;
    } else if (delivery.deliverAt) {
      const deliverAtDate = DateTime.fromISO(delivery.deliverAt);
      if (deliverAtDate < DateTime.local().endOf('day')) {
        errors.deliverAt = genericDeliverAtError;
      } else if (deliverAtDate > DateTime.local().plus({ years: 1 })) {
        errors.deliverAt = t('delivery_date_must_be_less_than_a_year_in_the_future');
      }
    }

    const hasErrors = Object.keys(errors).length > 0;
    if (hasErrors) {
      setErrors(errors);
    } else {
      events.giftCardAddedToBag({
        priceInCents: product.priceInCents,
        productName: product.name,
        sku: product.sku,
        currency,
      });
      dispatch(
        storeCartActions.addGiftCardCartItem({
          sku: product.sku,
          giftCardDelivery: delivery,
          currency,
        }),
      );
      setAddedToBag(true);
    }
  };

  return { addToBag, addedToBag };
}
